<template>
    <div class="RiskProfileCriteriaTableRowTitle flex flex-col gap-1 flex-1">
        <span
            class="text-[14.25px] font-medium leading-none"
            :class="[ isVerify ? 'text-eidDarkBlue' : 'text-eidTechGray' ]"
        >
            {{ displayMetadata.title }}
        </span>

        <span
            v-if="isVerify && criterionInputHint"
            class="RiskProfileCriteriaTableRowTitle__hint--light"
            v-html="criterionInputHint"
        />
        <span v-else-if="!isVerify" class="RiskProfileCriteriaTableRowTitle__hint--light">
            Will be collected but not verified
        </span>
    </div>
</template>

<style lang="scss">
.RiskProfileCriteriaTableRowTitle {
    &__hint--dark,
    &__hint--light {
        color: var(--eidDarkBlue);
        font-size: 12.5px;
        line-height: 1;
    }

    &__hint--dark {
        color: var(--eidDarkBlue);
    }

    &__hint--light {
        color: var(--eidTechGray);
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import JsonSchema from '@evidentid/json-schema/interfaces/JsonSchema';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionDisplayMeta } from '@evidentid/tprm-portal-lib/models/decisioning';
    import { CriterionField } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';

    import { castYesNoTextToBoolean } from '@/modules/decisioning-criteria/utils/castYesNoTextToBoolean';

    const configInSettingsText = 'Configuration in Settings';

    export default defineComponent({
        name: 'RiskProfileCriteriaTableRowTitle',
        props: {
            isVerify: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            hasAdvancedSettings: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            displayMetadata: {
                type: Object as PropType<CriterionDisplayMeta>,
                default: () => ({} as CriterionDisplayMeta),
            },
            displayReference: {
                type: Object as PropType<CriterionField | null>,
                default: () => null as CriterionField | null,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [] as CustomProperty[],
            },
            // This is only used for GL Aggregate Limit
            isVisibleValueDependentFieldPlaceholder: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        computed: {
            criterionInputHint(): string | null {
                const hasAdvancedSettingsText = this.displayMetadata.nonInputCriterionText ?
                    [ 'Configure required value in advanced settings.', 'Configure requirements in advanced settings.' ]
                        .includes(this.displayMetadata.nonInputCriterionText)
                    : false;
                if (hasAdvancedSettingsText) {
                    return configInSettingsText;
                }
                const { condition, nonInputCriterionText } = this.displayMetadata;
                if (this.displayValue != null && this.displayValue !== '') {
                    return condition ?
                        `${condition} <strong>${this.displayValue}</strong>`
                        : this.displayValue;
                }
                if (this.displayReference) {
                    return configInSettingsText;
                }
                return condition || nonInputCriterionText || null;
            },
            displayValue(): string | null {
                if (!this.displayReference || this.isVisibleValueDependentFieldPlaceholder) {
                    return null;
                }
                const { schema, value } = this.displayReference as any;
                if (value == null) {
                    return null;
                }
                return this.formatValueBySchema(schema, value);
            },
        },
        methods: {
            formatCurrency(value: number): string {
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                }).format(value);
            },
            formatEnum(value: string | number, schema: any): string {
                const enumValueIndex = schema.enum.indexOf(value);
                return schema.enumLabels?.[enumValueIndex] || value;
            },
            formatInsuredField(value: string): string {
                const customProperty = this.customProperties
                    .find((customProperty) => customProperty.key === value);
                return customProperty?.name || value;
            },
            formatArray(value: any[], schema: any): string {
                if (schema.items.type === 'string' && ('enumLabels' in schema.items && schema.items.enum)) {
                    return value.map((item: string) => {
                        const enumValueIndex = schema.items.enum.indexOf(item);
                        return schema.items.enumLabels?.[enumValueIndex] || item;
                    }).join(', ');
                }
                return value.join(', ');
            },
            formatObject(value: any, schema: any): string | null {
                if (schema.properties.isBasedOnInsuredField) {
                    if (value.isBasedOnInsuredField) {
                        if (value.insuredField == null) {
                            return null;
                        }
                        return this.formatInsuredField(value.insuredField);
                    }
                    if (value.value != null) {
                        return this.formatValueBySchema(schema.else?.properties?.value, value?.value);
                    }
                }
                // limitSource (the Limit criterion on Leased or Rented Equipment) discriminates
                // on `mode` rather than a boolean flag. SPECIFIED carries a plain amount;
                // ENTITY_FIELD and ENTITY_EQUIPMENT both carry an insuredField reference and
                // render the same way as any other insured-field lookup.
                if (schema.properties.mode) {
                    if (value.mode === 'SPECIFIED') {
                        if (value.value == null) {
                            return null;
                        }
                        return this.formatValueBySchema(schema.then?.properties?.value, value.value);
                    }
                    if (value.mode === 'ENTITY_FIELD' || value.mode === 'ENTITY_EQUIPMENT') {
                        if (value.insuredField == null) {
                            return null;
                        }
                        return this.formatInsuredField(value.insuredField);
                    }
                }
                return null;
            },
            /** Boolean criterion values read as Yes/No throughout the product: the input control's own
             * options, bulk import's "Must contain Yes/No" validation, and the entity views all use
             * those labels. The criterion's own enumValues cannot supply them -- its keys are the
             * "YES"/"NO" text while the value is a boolean -- so they come from the schema instead.
             */
            formatBoolean(value: any): string | null {
                const asBoolean = castYesNoTextToBoolean(value, { type: 'boolean' } as JsonSchema);
                return typeof asBoolean === 'boolean' ? (asBoolean ? 'Yes' : 'No') : null;
            },
            formatValueBySchema(schema: any, value: any): string | null {
                if (schema.type === 'number' && schema.displayFormat === 'CURRENCY') {
                    return this.formatCurrency(value);
                }
                if (schema.type === 'boolean') {
                    return this.formatBoolean(value) ?? value;
                }
                if ([ 'string', 'integer' ].includes(schema.type) && schema.displayFormat === 'ENUM') {
                    return this.formatEnum(value, schema);
                }
                if (schema.type === 'string' && schema.displayFormat === 'INSURED_FIELD') {
                    return this.formatInsuredField(value);
                }
                if (schema.type === 'array') {
                    return this.formatArray(value, schema);
                }
                if (schema.type === 'object') {
                    return this.formatObject(value, schema);
                }
                return value;
            },
        },
    });
</script>
